import { useEffect, useMemo, useState } from 'react'
import { ENVIO_A_CARGO_COMPRADOR } from '../../../constants/mediosEnviosPagos.js'
import { validarRangoFechas } from '../../../helpers/dateValidation'
import * as listaEnvioService from '../../servicios/listaEnvioService'
import * as publicacionesService from '../../servicios/publicacionesService'

const obtenerIdItem = (item) => item.idPresupuestoItem || item.idPublicacion

export const useListaEnvio = ({
  idVendedor,
  pedidoItems,
  idMedioEnvio,
  direccionEntrega,
  medioPagoSeleccionado
}) => {
  const [mediosEnvios, setMediosEnvio] = useState([])
  const [listasEnvio, setListasEnvio] = useState({})
  const [listaEnvioSeleccionada, setListaEnvioSeleccionada] = useState({})

  useEffect(() => {
    obtenerMediosEnvio()
    obtenerListasEnvioDelVendedor()
  }, [])

  const obtenerMediosEnvio = async () => {
    const { data } = await publicacionesService.buscarMedioEnvio()
    setMediosEnvio(data.map((m) => ({ value: m.idMedioEnvio, label: m.nombre })))
  }

  const obtenerListasEnvioDelVendedor = async () => {
    const listasEnvioPorProducto = {}

    for (const item of pedidoItems) {
      const { data } = await listaEnvioService.buscarListaPorVendedor(
        idVendedor,
        item.idPublicacion
      )
      const listasValidas = data.filter((lista) => {
        if (!validarRangoFechas(new Date(), lista.vigenciaHasta)) {
          return false
        }

        return !!(lista.grilla && lista.grilla.length > 0)
      })

      const listasFormateadas = listasValidas.map((l) => {
        const filaAMostrar = l.grilla[0] || {}

        return {
          value: `${item.idPublicacion}-${l.idLista}`,
          idLista: l.idLista,
          label: `${l.nombreLista} | ${filaAMostrar.zona} | ${filaAMostrar.cantidadDesde}-${filaAMostrar.cantidadHasta} | $${filaAMostrar.costo}`,
          nombreLista: l.nombreLista,
          zona: filaAMostrar.zona,
          cantidadDesde: filaAMostrar.cantidadDesde,
          cantidadHasta: filaAMostrar.cantidadHasta,
          precio: filaAMostrar.costo,
          grilla: l.grilla
        }
      })

      listasEnvioPorProducto[obtenerIdItem(item)] = listasFormateadas
    }

    setListasEnvio(listasEnvioPorProducto)
  }

  const handleClickListaEnvio = (lista, idItem) => {
    setListaEnvioSeleccionada((prev) => ({
      ...prev,
      [idItem]: lista
    }))
  }

  const obtenerFilaEnvio = (lista, cantidadTotal) => {
    if (!lista?.grilla || lista.grilla.length === 0) {
      return null
    }

    return (
      lista.grilla.find((fila) => {
        return (
          cantidadTotal >= Number(fila.cantidadDesde) && cantidadTotal <= Number(fila.cantidadHasta)
        )
      }) || null
    )
  }

  const detectarConflictoListasPorDomicilio = (items) => {
    const grupos = new Map()

    items.forEach((item) => {
      if (idMedioEnvio[obtenerIdItem(item)] !== ENVIO_A_CARGO_COMPRADOR) {
        return
      }

      const listaSeleccionada = listaEnvioSeleccionada[obtenerIdItem(item)]
      if (!listaSeleccionada) {
        return
      }

      const idUsuarioDomicilio = direccionEntrega[obtenerIdItem(item)]?.idUsuarioDomicilio
      if (idUsuarioDomicilio === undefined || idUsuarioDomicilio === null) {
        return
      }

      const idMedioPago = Number(medioPagoSeleccionado[obtenerIdItem(item)])

      const claveGrupo = `${idUsuarioDomicilio}-${idMedioPago}`
      const grupo = grupos.get(claveGrupo) || []
      grupo.push({ idItem: obtenerIdItem(item), idLista: Number(listaSeleccionada.idLista) })
      grupos.set(claveGrupo, grupo)
    })

    const idsItemEnConflicto = new Set()

    grupos.forEach((itemsDelGrupo) => {
      const idsListaDistintos = new Set(itemsDelGrupo.map((i) => i.idLista))
      if (idsListaDistintos.size > 1) {
        itemsDelGrupo.forEach((i) => idsItemEnConflicto.add(i.idItem))
      }
    })

    return idsItemEnConflicto
  }

  const agruparCantidadesPorListaSeleccionada = () => {
    const grupos = new Map()

    const idsItemEnConflicto = detectarConflictoListasPorDomicilio(pedidoItems)

    pedidoItems.forEach((item) => {
      if (idMedioEnvio[obtenerIdItem(item)] !== ENVIO_A_CARGO_COMPRADOR) {
        return
      }

      if (idsItemEnConflicto.has(obtenerIdItem(item))) {
        return
      }

      const listaSeleccionada = listaEnvioSeleccionada[obtenerIdItem(item)]
      if (!listaSeleccionada) {
        return
      }

      const idLista = Number(listaSeleccionada.idLista)
      const existente = grupos.get(idLista)

      if (existente) {
        existente.cantidadTotal += Number(item.cantidad) || 0
      } else {
        grupos.set(idLista, {
          idLista,
          cantidadTotal: Number(item.cantidad) || 0,
          lista: listaSeleccionada
        })
      }
    })

    return grupos
  }

  const obtenerPrecioEnvioPorItem = (item) => {
    const listaSeleccionada = listaEnvioSeleccionada[obtenerIdItem(item)]

    if (!listaSeleccionada) {
      return 0
    }

    const grupos = agruparCantidadesPorListaSeleccionada()
    const grupo = grupos.get(Number(listaSeleccionada.idLista))

    if (!grupo) {
      return 0
    }

    const filaEnvio = obtenerFilaEnvio(grupo.lista, grupo.cantidadTotal)
    return Number(filaEnvio?.costo || 0)
  }

  const obtenerItemsDelMismoGrupo = (item) => {
    const idItem = obtenerIdItem(item)
    const idMedioPagoItem = medioPagoSeleccionado[idItem]
    const idUsuarioDomicilioItem = direccionEntrega[idItem]?.idUsuarioDomicilio
    const faltaDomicilioItem =
      idUsuarioDomicilioItem === undefined || idUsuarioDomicilioItem === null

    return pedidoItems.filter((otro) => {
      const idOtro = obtenerIdItem(otro)
      if (idMedioEnvio[idOtro] !== ENVIO_A_CARGO_COMPRADOR) {
        return false
      }
      const idMedioPagoOtro = medioPagoSeleccionado[idOtro]
      if (idMedioPagoOtro !== idMedioPagoItem) {
        return false
      }

      if (faltaDomicilioItem) {
        return true
      }

      const idUsuarioDomicilioOtro = direccionEntrega[idOtro]?.idUsuarioDomicilio
      return idUsuarioDomicilioOtro === idUsuarioDomicilioItem
    })
  }

  const calcularCantidadTotalGrupoItem = (item) => {
    const itemsDelGrupo = obtenerItemsDelMismoGrupo(item)
    return itemsDelGrupo.reduce((acc, i) => acc + (Number(i.cantidad) || 0), 0)
  }

  const evaluarEstadoListaEnvioPorItem = (item) => {
    const itemsDelMismoGrupo = obtenerItemsDelMismoGrupo(item)

    const listasMap = new Map()

    itemsDelMismoGrupo.forEach((itemDelGrupo) => {
      const listaAplicable = listaEnvioSeleccionada[obtenerIdItem(itemDelGrupo)]
      if (listaAplicable) {
        const idLista = Number(listaAplicable.idLista)
        if (listasMap.has(idLista)) {
          const existente = listasMap.get(idLista)
          existente.cantidadTotal += Number(itemDelGrupo.cantidad) || 0
        } else {
          listasMap.set(idLista, {
            cantidadTotal: Number(itemDelGrupo.cantidad) || 0,
            listaInfo: listaAplicable
          })
        }
      }
    })

    if (listasMap.size > 1) {
      return { debeConsultar: true, motivo: 'multiplesListas' }
    }

    for (const [, info] of listasMap) {
      const cantidadTotal = info.cantidadTotal
      const listaInfo = info.listaInfo

      if (!listaInfo || !listaInfo.grilla || listaInfo.grilla.length === 0) {
        return { debeConsultar: true, motivo: 'sinGrilla' }
      }

      const filaValida = obtenerFilaEnvio(listaInfo, cantidadTotal)

      if (!filaValida) {
        return { debeConsultar: true, motivo: 'cantidadSuperaMáximo' }
      }
    }

    return { debeConsultar: false, motivo: null }
  }

  const calcularCostoEnvioTotal = () => {
    const grupos = agruparCantidadesPorListaSeleccionada()
    const precios = Array.from(grupos.values())
      .map((grupo) => {
        const filaEnvio = obtenerFilaEnvio(grupo.lista, grupo.cantidadTotal)
        return Number(filaEnvio?.costo || 0)
      })
      .filter((precio) => precio > 0)

    return precios.length > 0 ? Math.max(...precios) : 0
  }

  const idsItemConConflictoListas = useMemo(() => {
    return detectarConflictoListasPorDomicilio(pedidoItems)
  }, [pedidoItems, idMedioEnvio, listaEnvioSeleccionada, direccionEntrega, medioPagoSeleccionado])

  const costoEnvio = useMemo(
    () => calcularCostoEnvioTotal(),
    [pedidoItems, idMedioEnvio, listaEnvioSeleccionada, direccionEntrega, medioPagoSeleccionado]
  )

  const resetListaEnvioSeleccionada = () => setListaEnvioSeleccionada({})

  const obtenerEstadoCelda = (item) => {
    const idItem = obtenerIdItem(item)
    const listasCargadasDelProducto = listasEnvio[idItem] || []

    if (listasCargadasDelProducto.length === 0) {
      return { tipo: 'consultar', motivo: '(Sin lista de envío disponible)' }
    }

    const idMedioPagoSeleccionado = medioPagoSeleccionado[idItem]
    const faltaMedioPago = idMedioPagoSeleccionado === undefined || idMedioPagoSeleccionado === null
    const idUsuarioDomicilioSeleccionado = direccionEntrega[idItem]?.idUsuarioDomicilio
    const faltaDomicilio =
      idUsuarioDomicilioSeleccionado === undefined || idUsuarioDomicilioSeleccionado === null

    if (faltaMedioPago) {
      const listasPorCantidadItem = listasCargadasDelProducto.filter((lista) =>
        Boolean(obtenerFilaEnvio(lista, Number(item.cantidad) || 0))
      )
      return { tipo: 'select', opciones: listasPorCantidadItem }
    }

    if (faltaDomicilio) {
      const listasPorCantidadItem = listasCargadasDelProducto.filter((lista) =>
        Boolean(obtenerFilaEnvio(lista, Number(item.cantidad) || 0))
      )
      return { tipo: 'select', opciones: listasPorCantidadItem }
    }

    const cantidadTotalGrupo = calcularCantidadTotalGrupoItem(item)
    const listasDelProducto = listasCargadasDelProducto.filter((lista) =>
      Boolean(obtenerFilaEnvio(lista, cantidadTotalGrupo))
    )

    if (listasDelProducto.length === 0) {
      return {
        tipo: 'consultar',
        motivo: `(Sin lista para cantidad ${cantidadTotalGrupo})`
      }
    }

    if (idsItemConConflictoListas.has(idItem)) {
      return {
        tipo: 'selectConAviso',
        opciones: listasDelProducto,
        aviso: 'Precio a definir por el vendedor (múltiples listas en carrito)'
      }
    }

    const resultado = evaluarEstadoListaEnvioPorItem(item)

    if (resultado.debeConsultar) {
      const mensajeMotivo =
        resultado.motivo === 'cantidadSuperaMáximo'
          ? 'Cantidad total supera máximo disponible'
          : 'Configuración inválida'
      return { tipo: 'consultar', motivo: `(${mensajeMotivo})` }
    }

    return { tipo: 'select', opciones: listasDelProducto }
  }

  return {
    mediosEnvios,
    listasEnvio,
    listaEnvioSeleccionada,
    handleClickListaEnvio,
    resetListaEnvioSeleccionada,
    obtenerFilaEnvio,
    obtenerPrecioEnvioPorItem,
    calcularCantidadTotalGrupoItem,
    evaluarEstadoListaEnvioPorItem,
    detectarConflictoListasPorDomicilio,
    idsItemConConflictoListas,
    obtenerEstadoCelda,
    costoEnvio
  }
}
